import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { listIdeas } from "../api/ideas";
import IdeaCard from "../components/ideas/IdeaCard";
import IdeaCategoryFilter from "../components/ideas/IdeaCategoryFilter";
import IdeasTopbar from "../components/ideas/IdeasTopbar";
import IdeaCreateModal from "../components/ideas/IdeaCreateModal";
import IdeaCardSkeleton from "../components/ideas/IdeaCardSkeleton";
import { PlusIcon, InboxIcon } from "../components/ideas/IdeasIcons";
import { Button, EmptyState, fmtNum, plural } from "../components/ui";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/ideas.css";

/**
 * Полный список идей с фильтрами и пагинацией («Загрузить ещё»).
 * Параметры синкаются с URL (можно делиться ссылкой).
 */
export default function IdeasListPage() {
  usePageTitle("Все идеи — Рублокс");
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const filters = useMemo(
    () => ({
      category: params.get("category") || null,
      status: params.get("status") || null,
      sort: params.get("sort") || "hot",
      q: params.get("q") || "",
    }),
    [params]
  );

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  /* Счётчик перезагрузок. `setFilters({})` для «Повторить» не годится:
     фильтры не меняются, эффект не перезапускается, кнопка молчит. */
  const [reloadTick, setReloadTick] = useState(0);

  const LIMIT = 20;

  /* Загрузка первой страницы при изменении фильтров. */
  useEffect(() => {
    const ctl = new AbortController();
    setBusy(true);
    setErr(null);
    listIdeas({ ...filters, page: 1, limit: LIMIT }, { signal: ctl.signal })
      .then((d) => {
        setItems(d.items || []);
        setTotal(d.total || 0);
        setPage(1);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setErr(e.message);
      })
      .finally(() => setBusy(false));
    return () => ctl.abort();
  }, [filters, reloadTick]);

  const loadMore = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const d = await listIdeas({
        ...filters, page: page + 1, limit: LIMIT,
      });
      setItems((arr) => arr.concat(d.items || []));
      setPage((p) => p + 1);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const setFilters = (patch) => {
    const np = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v) np.delete(k);
      else np.set(k, v);
    });
    setParams(np, { replace: true });
  };

  const handlePropose = () => {
    if (!isAuthenticated) {
      navigate("/login?back=/ideas/list");
      return;
    }
    setOpenModal(true);
  };

  const canLoadMore = items.length < total;

  return (
    <div className="idz-page">
      <IdeasTopbar />

      <main className="idz-container" style={{ paddingTop: 24 }}>
        <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 16, gap: 12, flexWrap: "wrap"
        }}>
          <h1 style={{ margin: 0, fontSize: "clamp(22px, 4vw, 28px)", fontWeight: 800, letterSpacing: "-0.4px" }}>
            Все идеи{" "}
            <span className="ui-num" style={{ color: "var(--idz-muted)", fontWeight: 600 }}>
              ({fmtNum(total)})
            </span>
          </h1>
          <button className="idz-btn-primary idz-hide-on-mobile" onClick={handlePropose}>
            <PlusIcon /> Предложить идею
          </button>
        </div>

        <IdeaCategoryFilter {...filters} onChange={setFilters} />

        {/* Ошибка с выходом. Раньше это была строка текста без действия:
            список пуст, причина непонятна, повторить нечем. */}
        {err && items.length === 0 ? (
          <EmptyState
            icon={<InboxIcon />}
            title="Не получилось загрузить идеи"
            desc="Проверь соединение и попробуй ещё раз."
            action={
              <Button variant="primary" onClick={() => setReloadTick((t) => t + 1)}>
                Повторить
              </Button>
            }
          />
        ) : null}
        {err && items.length > 0 && <div className="idz-err">{err}</div>}

        {items.length === 0 && busy ? (
          <IdeaCardSkeleton count={6} />
        ) : items.length === 0 && !busy && !err ? (
          <EmptyState
            icon={<InboxIcon />}
            title="Ничего не найдено"
            desc="По выбранным фильтрам идей нет. Попробуй сбросить их — или предложи свою."
            action={
              <Button variant="primary" onClick={handlePropose}>
                Предложить идею
              </Button>
            }
          />
        ) : (
          <div className="idz-row">
            {items.map((i) => <IdeaCard key={i.id} idea={i} />)}
          </div>
        )}

        {canLoadMore && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            {/* Раньше текст подменялся на «Загружаю…» и кнопка меняла
                ширину. Плюс не было склонения: «ещё 21 идей». */}
            <Button variant="ghost" onClick={loadMore} loading={busy}>
              Показать ещё {fmtNum(total - items.length)}{" "}
              {plural(total - items.length, "идею", "идеи", "идей")}
            </Button>
          </div>
        )}
      </main>

      <button className="idz-fab" onClick={handlePropose} aria-label="Предложить идею">
        <PlusIcon /> Идея
      </button>

      <IdeaCreateModal open={openModal} onClose={() => setOpenModal(false)} />
    </div>
  );
}
